import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KpiCard, type KpiCardProps } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { ProductImageHealth } from "@/components/ProductImageHealth";
import { RangeFilter } from "@/components/RangeFilter";
import { ExportCsvButton } from "@/components/ExportCsvButton";
import { buildTopCsv, buildStockValueHistoryCsv } from "@/lib/analytics-csv";
import { Donut } from "@/components/charts/Donut";
import { StackedBarChart, CHANNEL_COLORS } from "@/components/charts/StackedBarChart";
import { ChannelLineChart } from "@/components/charts/ChannelLineChart";
import {
  useDashboard,
  useDashSeries,
  useDashSummary,
  useTop,
  useStockValueHistory,
  DASH_SERIES_METRICS,
  type DashRange,
  type DashSeriesMetric,
} from "@/api/dashboard";
import { UNIT_LABELS, formatBucketLabel } from "@/lib/timeseries-format";
import {
  DASH_RANGE_LABELS,
  DASH_METRIC_LABELS,
  DASH_METRIC_SUBTITLES,
  formatDashValue,
  formatDashPercent,
  formatEuro,
  sumDashBuckets,
  needsHistoryHint,
  HISTORY_HINT_TEXT,
} from "@/lib/dash-format";
import { bucketTotals, halfPeriodTrend } from "@/lib/revenue-trend";

// ============================================================
//  Dekorative Kachel-Grundformen + Versandstatus-Fallback — vorher in ./mockData.ts als
//  Vollausfall-Fallback fuer die GESAMTE KPI-Zeile. Task 15: mockData.ts entfaellt
//  (CATEGORIES wird durch echte Top-Kategorien ersetzt), die verbleibenden Bausteine
//  wandern als schlanke Inline-Konstanten hierher. Wichtig: Delta/Richtung/Sparkline sind
//  bewusst STATISCH — kein Backend-Endpoint liefert eine Zeitreihe fuer diese vier
//  Kennzahlen (offene Auftraege / Lagerwarnungen / Gutschriftquote / Lagerwert), einen
//  Trend zu erfinden waere eine unbelegte Behauptung. "Live" markiert nur "wird aktiv
//  verfolgt", value/meta kommen unabhaengig je Karte aus useDashboard()/useDashSummary().
const KPI_SPARK_UP = [8, 10, 9, 12, 11, 14, 13, 16, 15, 18, 17, 20];
const KPI_SPARK_DOWN = [18, 17, 16, 17, 15, 14, 15, 13, 12, 12, 11, 10];
const KPI_SPARK_WAVE = [10, 12, 11, 13, 14, 13, 15, 16, 15, 17, 16, 18];
const KPI_SPARK_FLAT = [12, 13, 12, 14, 13, 14, 13, 14, 14, 13, 14, 15];

const KPI_SHAPE: Array<Omit<KpiCardProps, "value" | "meta">> = [
  { label: "Umsatzentwicklung", delta: "", dir: "up", spark: KPI_SPARK_UP },
  { label: "Lagerwarnungen", delta: "Live", dir: "down", spark: KPI_SPARK_WAVE, accent: "coral" },
  { label: "Gutschriftquote", delta: "Live", dir: "up", spark: KPI_SPARK_DOWN },
  { label: "Lagerwert (EK)", delta: "Live", dir: "up", spark: KPI_SPARK_FLAT },
];

const KPI_LOADING: Omit<KpiCardProps, "label" | "delta" | "dir" | "spark" | "accent"> = {
  value: "…",
  meta: "lädt…",
};
const KPI_UNAVAILABLE: Omit<KpiCardProps, "label" | "delta" | "dir" | "spark" | "accent"> = {
  value: "—",
  meta: "nicht erreichbar",
};

const SHIPMENT_FALLBACK: { label: string; count: number; color: string }[] = [
  { label: "In Bearbeitung", count: 84, color: "var(--blue)" },
  { label: "Kommissioniert", count: 62, color: "var(--primary)" },
  { label: "Versendet", count: 138, color: "var(--success)" },
  { label: "Retour-Eingang", count: 9, color: "var(--coral)" },
];

export default function Overview() {
  const { data: dashboard, isLoading, isError } = useDashboard();

  const [range, setRange] = useState<DashRange>("30T");
  const [metric, setMetric] = useState<DashSeriesMetric>("revenue");
  const [drill, setDrill] = useState(false);
  const [trendDialogOpen, setTrendDialogOpen] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [stockRange, setStockRange] = useState<DashRange>("90T");

  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useDashSummary(range);
  const { data: stockHistory, isLoading: stockHistoryLoading } = useStockValueHistory(stockRange);
  const stockHistoryRows = stockHistory?.rows ?? [];
  const stockHistoryBuckets = stockHistoryRows.map((r) => ({
    label: r.snapshot_date.slice(5), // MM-DD — kompakte X-Achse
    values: { Lagerwert: r.total_value_ek },
  }));
  const { data: series, isLoading: seriesLoading, isError: seriesError } = useDashSeries(metric, range, drill);
  // Codex-Finding A: die Kachel „Umsatzentwicklung" (und ihr Vollbild-Dialog) ist fest auf
  // die Metrik "revenue" gepinnt — sie darf NICHT der umschaltbaren Chart-Metrik folgen,
  // sonst zeigt sie z. B. Gutschriften unter einem Umsatz-Titel. TanStack Query dedupliziert
  // per queryKey: bei metric === "revenue" ist das derselbe Cache-Eintrag, kein Extra-Request.
  const { data: revSeries, isLoading: revLoading, isError: revError } = useDashSeries("revenue", range, drill);
  const [categoriesRange, setCategoriesRange] = useState<DashRange>("30T");
  const { data: categoryPayload, isLoading: categoriesLoading, isError: categoriesError } = useTop(
    "category",
    categoriesRange,
    "revenue",
  );

  const [topProductsRange, setTopProductsRange] = useState<DashRange>("30T");
  const { data: topGaPayload, isLoading: topGaLoading, isError: topGaError } = useTop(
    "ga",
    topProductsRange,
    "qty",
  );
  const topGaRows = (topGaPayload?.rows ?? []).slice(0, 5);

  const anyError = isError || summaryError || seriesError || revError || categoriesError || topGaError;

  const seriesUnit = series?.unit ?? "day";
  const revSeriesUnit = revSeries?.unit ?? "day";

  // ------------------------------------------------------------
  //  KPI-Zeile: 4 Karten, jede unabhaengig aus ihrer eigenen Quelle befuellt (nicht mehr
  //  ein einziger Alles-oder-Nichts-Fallback wie vorher). Karte 1 aus der auf "revenue"
  //  gepinnten useDashSeries (Task 7 + Codex-Finding A), Karte 2 aus useDashboard()
  //  (bestehender /system/dashboard/-Endpoint, unveraendert), Karte 3+4 aus
  //  useDashSummary(range) (Task 13, neu).
  // ------------------------------------------------------------
  const revenueTrendCard = useMemo<KpiCardProps>(() => {
    const shape = KPI_SHAPE[0]!;
    if (revLoading && !revSeries) return { ...shape, ...KPI_LOADING };
    if (revError || !revSeries) return { ...shape, ...KPI_UNAVAILABLE };
    const totals = bucketTotals(revSeries.buckets, revSeries.channels);
    const trend = halfPeriodTrend(totals);
    const sum = totals.reduce((a, b) => a + b, 0);
    const avg = totals.length > 0 ? sum / totals.length : 0;
    return {
      ...shape,
      value: formatDashValue("revenue", sum),
      delta: trend.pct === null ? "—" : `${trend.pct > 0 ? "+" : ""}${trend.pct} % (2. vs. 1. Hälfte)`,
      dir: trend.dir,
      spark: totals.length >= 2 ? totals : KPI_SPARK_FLAT,
      meta: `${DASH_METRIC_LABELS["revenue"]} · ${DASH_RANGE_LABELS[range]} · Ø ${formatDashValue("revenue", avg)}/${UNIT_LABELS[revSeriesUnit]}`,
    };
  }, [revSeries, revLoading, revError, range, revSeriesUnit]);

  const lowStockCard = useMemo<KpiCardProps>(() => {
    const shape = KPI_SHAPE[1]!;
    if (isLoading && !dashboard) return { ...shape, ...KPI_LOADING };
    if (isError || !dashboard) return { ...shape, ...KPI_UNAVAILABLE };
    return {
      ...shape,
      value: dashboard.kpis.low_stock.toLocaleString("de-DE"),
      meta: "kritische + niedrige SKUs",
    };
  }, [dashboard, isLoading, isError]);

  const creditRateCard = useMemo<KpiCardProps>(() => {
    const shape = KPI_SHAPE[2]!;
    if (summaryLoading && !summary) return { ...shape, ...KPI_LOADING };
    if (summaryError || !summary) return { ...shape, ...KPI_UNAVAILABLE };
    return {
      ...shape,
      value: formatDashPercent(summary.credit_rate),
      meta: `${formatDashValue("credits", summary.credits)} von ${formatDashValue("revenue", summary.revenue)} Umsatz · ${DASH_RANGE_LABELS[range]}`,
    };
  }, [summary, summaryLoading, summaryError, range]);

  const stockValueCard = useMemo<KpiCardProps>(() => {
    const shape = KPI_SHAPE[3]!;
    if (summaryLoading && !summary) return { ...shape, ...KPI_LOADING };
    if (summaryError || !summary) return { ...shape, ...KPI_UNAVAILABLE };
    // ga_without_ek: Grundartikel ohne hinterlegten Einkaufspreis zaehlen im Lagerwert
    // mit 0 EUR (Backend-Regel, s. buildStockValueSql) — der ausgewiesene Wert ist damit
    // eine Untergrenze. Das bleibt hier sichtbar, statt es stillschweigend zu verschweigen.
    const missing = summary.ga_without_ek > 0
      ? ` · ${summary.ga_without_ek.toLocaleString("de-DE")} ohne EK-Preis (Untergrenze)`
      : "";
    return {
      ...shape,
      value: formatDashValue("revenue", summary.stock_value_ek),
      meta: `${summary.ga_count.toLocaleString("de-DE")} Grundartikel${missing}`,
    };
  }, [summary, summaryLoading, summaryError]);

  const shipmentSegments = useMemo(() => {
    if (!dashboard?.shipment_states) return SHIPMENT_FALLBACK;
    const s = dashboard.shipment_states;
    const arr = [
      { label: "Offen", count: s.open ?? 0, color: "#94A3B8" },
      { label: "Bezahlt", count: s.paid ?? 0, color: "#0EA5E9" },
      { label: "Verpackt", count: s.packed ?? 0, color: "#F59E0B" },
      { label: "Versendet", count: s.shipped ?? 0, color: "#A78BFA" },
      { label: "Zugestellt", count: s.arrived ?? 0, color: "#10B981" },
      { label: "Storno", count: s.cancelled ?? 0, color: "#EF4444" },
    ].filter((x) => x.count > 0);
    return arr.length > 0 ? arr : SHIPMENT_FALLBACK;
  }, [dashboard]);

  const totalShipments = shipmentSegments.reduce((s, x) => s + x.count, 0);

  // ------------------------------------------------------------
  //  Kanal-Stack-Diagramm (Task 14 StackedBarChart + Task 13 useDashSeries).
  // ------------------------------------------------------------
  const seriesChannels = series?.channels ?? [];
  const seriesBuckets = series?.buckets ?? [];
  // Codex-Finding A: der Vollbild-Dialog der Umsatz-Kachel rendert die gepinnte
  // Umsatz-Serie — unabhaengig davon, welche Metrik im Haupt-Chart gerade gewaehlt ist.
  const revChannels = revSeries?.channels ?? [];
  const revBuckets = revSeries?.buckets ?? [];
  const hasUnmatchedChannel = seriesChannels.includes("Nicht zugeordnet");
  const seriesSum = useMemo(() => sumDashBuckets(seriesBuckets, seriesChannels), [seriesBuckets, seriesChannels]);
  const seriesAvg = seriesBuckets.length > 0 ? seriesSum / seriesBuckets.length : 0;

  const categoryRows = (categoryPayload?.rows ?? []).slice(0, 8);

  return (
    <div className="p-8 space-y-5 max-w-[1400px]">
      {anyError && (
        <div className="rounded-md border border-coral/40 bg-coral-soft px-4 py-3 text-sm text-coral">
          Ein oder mehrere Dashboard-Bereiche sind aktuell nicht erreichbar — betroffene Kacheln und Panels zeigen einen Hinweis statt echter Zahlen.
        </div>
      )}
      {!isError && dashboard && (
        <div className="rounded-md border border-success/40 bg-success-soft px-4 py-2 text-xs text-success-foreground inline-flex items-center gap-2">
          <span className="size-2 rounded-full bg-success animate-pulse" />
          Live · letzte Aktualisierung {new Date(dashboard.generated_at).toLocaleTimeString("de-DE")}
        </div>
      )}

      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-ink">Produktmanagement</h1>
          <p className="text-sm text-ink-2 mt-1">Übersicht über Katalog, Bestand und Bestelllage</p>
        </div>
        <Button variant="outline" size="sm" className="gap-1.5"><Download className="size-3.5" />Export</Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard {...revenueTrendCard} onClick={() => setTrendDialogOpen(true)} />
        <KpiCard {...lowStockCard} />
        <KpiCard {...creditRateCard} />
        <KpiCard {...stockValueCard} onClick={() => setStockDialogOpen(true)} />
      </div>

      <ProductImageHealth />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel
          className="lg:col-span-2"
          title={`${DASH_METRIC_LABELS[metric]} · ${DASH_RANGE_LABELS[range]}`}
          subtitle={DASH_METRIC_SUBTITLES[metric]}
          actions={
            <div className="flex flex-wrap items-center justify-end gap-2">
              <RangeFilter value={range} onChange={setRange} label="Zeitraum" />
              <div className="flex p-0.5 rounded-md bg-panel-2 border border-line">
                {DASH_SERIES_METRICS.map((m) => (
                  <button
                    key={m}
                    onClick={() => setMetric(m)}
                    className={`px-2.5 py-1 text-xs rounded transition-colors ${metric === m ? "bg-background text-ink shadow-sm" : "text-ink-2 hover:text-ink"}`}
                  >
                    {DASH_METRIC_LABELS[m]}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setDrill((d) => !d)}
                aria-pressed={drill}
                className={`px-2.5 py-1 text-xs rounded-md border transition-colors whitespace-nowrap ${drill ? "border-primary bg-primary/10 text-primary" : "border-line text-ink-2 hover:text-ink"}`}
              >
                Amazon-Kanäle aufschlüsseln
              </button>
            </div>
          }
        >
          {seriesChannels.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-ink-2 mb-3">
              {seriesChannels.map((ch) => (
                <span key={ch} className="inline-flex items-center gap-1.5">
                  <span className="size-2 rounded-sm" style={{ background: CHANNEL_COLORS[ch] ?? CHANNEL_COLORS.Sonstige }} />
                  {ch}
                </span>
              ))}
              <span className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-sm" style={{ background: "var(--coral)" }} />
                Gutschrift (unabhängig vom Kanal)
              </span>
            </div>
          )}
          {needsHistoryHint(range) && (
            <div className="text-[11px] text-ink-2 italic mb-3">{HISTORY_HINT_TEXT}</div>
          )}
          {hasUnmatchedChannel && (
            <div className="text-[11px] text-ink-2 italic mb-3">
              „Nicht zugeordnet" ist kein Verkaufskanal — die Position konnte keinem Grundartikel zugeordnet werden.
            </div>
          )}

          {seriesError ? (
            <div className="text-sm text-coral py-8 text-center">Zeitreihe nicht erreichbar.</div>
          ) : seriesLoading && seriesBuckets.length === 0 ? (
            <div className="text-sm text-muted py-8 text-center">Lade Zeitreihe…</div>
          ) : seriesBuckets.length === 0 ? (
            <div className="text-sm text-muted py-8 text-center">Keine Daten im gewählten Zeitraum.</div>
          ) : (
            <>
              <StackedBarChart
                buckets={seriesBuckets.map((b) => ({ ...b, label: formatBucketLabel(b.label, seriesUnit) }))}
                channels={seriesChannels}
                formatValue={(n) => formatDashValue(metric, n)}
              />
              <div className="mt-3 text-xs text-muted font-mono text-right">
                Summe: {formatDashValue(metric, seriesSum)} · Ø {formatDashValue(metric, seriesAvg)} / {UNIT_LABELS[seriesUnit]}
              </div>
            </>
          )}
        </Panel>

        <Panel title="Versandstatus" subtitle="Bestellungen letzte 30 Tage">
          <div className="flex items-center gap-5">
            <Donut segments={shipmentSegments} />
            <div className="flex-1 space-y-2.5 text-xs">
              {shipmentSegments.map((s) => {
                const pct = Math.round((s.count / totalShipments) * 100);
                return (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="size-2 rounded-sm" style={{ background: s.color }} />
                    <span className="text-ink-2 flex-1">{s.label}</span>
                    <span className="font-mono text-ink font-semibold">{s.count}</span>
                    <span className="font-mono text-muted w-8 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel
          title="Top-Produkte"
          subtitle={`Nach verkaufter Menge · ${DASH_RANGE_LABELS[topProductsRange]}`}
          actions={
            <div className="flex items-center gap-2">
              <RangeFilter value={topProductsRange} onChange={setTopProductsRange} label="Zeitraum Top-Produkte" />
              <ExportCsvButton filename="top-produkte" build={() => buildTopCsv(topGaRows)} disabled={topGaRows.length === 0} />
              <a href="/analytics/articles" className="text-xs text-primary hover:underline whitespace-nowrap">Alle anzeigen →</a>
            </div>
          }
        >
          {topGaError ? (
            <div className="text-sm text-coral py-4">Top-Produkte nicht erreichbar.</div>
          ) : topGaLoading && topGaRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Lädt…</div>
          ) : topGaRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Keine Verkäufe im gewählten Zeitraum.</div>
          ) : (
            <ul className="divide-y divide-line-2">
              {topGaRows.map((p, i) => (
                <li key={p.id ?? `${p.label}-${i}`} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className="font-mono text-xs text-muted w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-ink truncate">{p.label || "—"}</div>
                    <div className="text-[11px] text-ink-2 font-mono truncate">{p.sublabel}</div>
                    <div className="mt-1 h-1 bg-panel-2 rounded"><div className="h-full bg-primary rounded" style={{ width: `${Math.min(100, Math.max(0, p.pct))}%` }} /></div>
                  </div>
                  <div className="font-mono text-sm text-ink font-semibold shrink-0">{p.qty.toLocaleString("de-DE")}</div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Task 13: Lagerwarnungen-Panel auf die Reichweiten-Seite umgezogen
            (pages/analytics/InventoryReach.tsx) — dort faellt die Nachbestell-Entscheidung.
            Zweite Grid-Spalte ist jetzt das Top-Kategorien-Panel (vorher unter dem Grid);
            die Kachel-Aufteilung ist im schmaleren Panel auf grid-cols-2 begrenzt. */}
        <Panel
          title="Top-Kategorien"
          subtitle={`Brutto-Umsatz · ${DASH_RANGE_LABELS[categoriesRange]}`}
          actions={
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted">Top {categoryRows.length} nach Umsatz</span>
              <RangeFilter value={categoriesRange} onChange={setCategoriesRange} label="Zeitraum Top-Kategorien" />
              <ExportCsvButton filename="top-kategorien" build={() => buildTopCsv(categoryRows)} disabled={categoryRows.length === 0} />
            </div>
          }
        >
          {categoriesError ? (
            <div className="text-sm text-coral py-4">Kategorien nicht erreichbar.</div>
          ) : categoriesLoading && categoryRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Lade Kategorien…</div>
          ) : categoryRows.length === 0 ? (
            <div className="text-sm text-muted py-4">Keine Verkäufe im gewählten Zeitraum.</div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {categoryRows.map((c) => (
                <div key={c.id ?? c.label} className="rounded-md border border-line-2 bg-background p-3 space-y-1.5">
                  <div className="text-xs text-ink truncate" title={c.label || "Ohne Kategorie"}>{c.label || "Ohne Kategorie"}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-base font-semibold text-ink">{formatEuro(c.value)}</span>
                    <span className="ml-auto text-[10px] text-muted font-mono">{c.pct.toFixed(0)}%</span>
                  </div>
                  <div className="text-[10px] text-muted font-mono">{c.qty.toLocaleString("de-DE")} Stück</div>
                  <div className="h-1 bg-panel-2 rounded"><div className="h-full bg-primary rounded" style={{ width: `${Math.min(100, Math.max(0, c.pct))}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Dialog open={trendDialogOpen} onOpenChange={setTrendDialogOpen}>
        {/* sm:max-w-5xl (nicht max-w-5xl): DialogContent bringt sm:max-w-lg mit — nur ein
            ebenfalls sm:-modifiziertes max-w wird von tailwind-merge ersetzt, ein
            unmodifiziertes max-w-5xl wuerde ab 640px vom sm:max-w-lg ueberstimmt. */}
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Umsatz je Kanal · {DASH_RANGE_LABELS[range]}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2">
            <RangeFilter value={range} onChange={setRange} label="Zeitraum Umsatzentwicklung" />
            {revChannels.map((ch) => (
              <span key={ch} className="inline-flex items-center gap-1.5">
                <span className="size-2 rounded-sm" style={{ background: CHANNEL_COLORS[ch] ?? CHANNEL_COLORS.Sonstige }} />
                {ch}
              </span>
            ))}
          </div>
          <ChannelLineChart
            buckets={revBuckets.map((b) => ({ ...b, label: formatBucketLabel(b.label, revSeriesUnit) }))}
            channels={revChannels}
            height={380}
            formatValue={(n) => formatDashValue("revenue", n)}
          />
        </DialogContent>
      </Dialog>

      {/* Welle 2 (#2): Lagerwert-Verlauf aus Tages-Snapshots. Historie waechst ab Einfuehrung —
          keine Rueckrechnung; der Leerzustand erklaert das. */}
      <Dialog open={stockDialogOpen} onOpenChange={setStockDialogOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Lagerwert (EK) · Verlauf · {DASH_RANGE_LABELS[stockRange]}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2">
            <RangeFilter value={stockRange} onChange={setStockRange} label="Zeitraum Lagerwert-Verlauf" />
            <ExportCsvButton
              filename="lagerwert-verlauf"
              build={() => buildStockValueHistoryCsv(stockHistoryRows)}
              disabled={stockHistoryRows.length === 0}
            />
          </div>
          {stockHistoryLoading && stockHistoryRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-ink-2">Lädt…</div>
          ) : stockHistoryRows.length === 0 ? (
            <div className="py-10 text-center text-sm text-ink-2">
              Noch keine Snapshots vorhanden — die Historie wächst ab Einführung
              (täglicher Snapshot 06:00 Uhr).
            </div>
          ) : (
            <>
              <ChannelLineChart
                buckets={stockHistoryBuckets}
                channels={["Lagerwert"]}
                colors={{ Lagerwert: "#0064d2", Sonstige: "#6b7280" }}
                height={380}
                formatValue={(n) => formatEuro(n)}
              />
              <div className="text-[11px] text-ink-2">
                {stockHistoryRows.length.toLocaleString("de-DE")} Tages-Snapshots ·
                zuletzt {stockHistoryRows[stockHistoryRows.length - 1]!.snapshot_date}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
